import { ReactNode } from 'react';
import { Badge, Box, SpaceBetween } from '@amzn/awsui-components-react';
import { Event, LocationType } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SpaLink } from '../../SpaLink';

const I18N = 'eventCardHeader';

/**
 * Location-type badge for an event card (single source for the Volunteer Activity tabs so they read
 * identically).
 */
function getEventLocationBadge(
  event: Event,
  t: (key: string) => string
): { label: string; color: 'green' | 'grey' | 'blue' } {
  const location = event.location;
  if (location?.locationType === LocationType.VIRTUAL) {
    return { label: t(`${I18N}.location.virtual`), color: 'grey' };
  }
  if (location?.amazonBuilding) {
    return { label: t(`${I18N}.location.amazonBuilding`), color: 'green' };
  }
  return { label: t(`${I18N}.location.inPerson`), color: 'blue' };
}

/**
 * Standard header chrome for an event-grouped card: title link + location badge, a caller-built meta
 * line (date • city, state • counts) with an optional inline extra (e.g. the Waitlists tab's Info
 * link), and the first community organization. Passed to VolunteerEventShiftsTable's header slot so
 * each event renders as its own card.
 */
export function EventCardHeader({
  event,
  meta,
  metaExtra,
}: {
  event: Event;
  meta: string;
  metaExtra?: ReactNode;
}) {
  const { t } = useTranslation(['activity', 'translation']);
  const [searchParams] = useSearchParams();

  // Forward the flag/timezone overrides so navigating to the event keeps the same feature view
  // (matters for ?features=ShiftWaitlist driven UAT sessions — mirrors SignupHeader's URL building).
  const urlFeatures = searchParams.get('features');
  const timezone = searchParams.get('timezone');
  const viewDetailsUrl = `/view-event?id=${event.eventId}${timezone ? '&timezone=' + timezone : ''}${
    urlFeatures ? '&features=' + urlFeatures : ''
  }`;

  const badge = getEventLocationBadge(event, t);
  const organization = event.communityOrganizations?.[0]?.name;

  return (
    <SpaceBetween size="s">
      <SpaceBetween direction="horizontal" size="xs">
        <SpaLink href={viewDetailsUrl}>
          <h2 style={{ margin: 0 }}>{event.title}</h2>
        </SpaLink>
        <Badge color={badge.color}>{badge.label}</Badge>
      </SpaceBetween>
      {(meta || metaExtra) && (
        <Box color="text-body-secondary" fontSize="body-s">
          <SpaceBetween direction="horizontal" size="s">
            {meta && <span>{meta}</span>}
            {metaExtra}
          </SpaceBetween>
        </Box>
      )}
      {organization && (
        <Box color="text-body-secondary" fontSize="body-s">
          {organization}
        </Box>
      )}
    </SpaceBetween>
  );
}
